import { MockDebug } from '@tino-frontend/mocks'
import terms from '@fixtures/apis/checkout/pending-terms-response.json'
import paymentReservation from '@fixtures/apis/checkout/payment-reservation-response.json'
import {
  purchaseIntentionAPIMock,
  PurchaseIntentionWithCashPaymentAPIMock,
  PurchaseIntentionWithDiscountAPIMock,
  JOHN_WICK_SELLER_ID,
  NewPurchaseIntentionAPIMock,
} from '@fixtures/apis/checkout'
import {
  REACT_APP_AUTH_API_URL,
  REACT_APP_CHECKOUT_BFF_API_URL,
  REACT_APP_MERCHANT_BFF_API_URL,
} from 'constants/envs'
import {
  Frequency,
  PurchaseIntentionStatus,
  ReservationError,
} from 'constants/enums'
import { PurchaseIntentionErrorCodes } from '@types'

const MockDebugTerms: MockDebug = {
  title: 'Recuperação de termos pendentes (Terms)',
  path: `${REACT_APP_CHECKOUT_BFF_API_URL}/purchase-intentions/:id/terms`,
  method: 'get',
  delay: 0,
  options: [
    {
      id: 'mock-terms',
      title: 'Com pendencia',
      status: 200,
      data: terms,
    },
    {
      id: 'mock-terms-empty',
      title: 'Sem pendencia',
      status: 200,
      data: [],
    },
    {
      id: 'mock-terms-bad-request',
      title: '[Erro] Bad Request',
      status: 400,
      data: {
        message: 'Bad Request',
      },
    },
    {
      id: 'mock-terms-interal-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {
        message: 'Internal Server Error',
      },
    },
  ],
}

const MockDebugReservation: MockDebug = {
  title: 'Criação da reserva (Reservation)',
  path: `${REACT_APP_CHECKOUT_BFF_API_URL}/v2/order/reservation`,
  method: 'post',
  delay: 3000,
  options: [
    {
      id: 'mock-reservation-success',
      title: 'Sucesso',
      status: 200,
      data: paymentReservation,
    },
    {
      id: 'mock-reservation-bad-request',
      title: '[Erro] Bad Request',
      status: 400,
      data: {
        message: 'Bad Request',
      },
    },
    {
      id: 'mock-reservation-duplicated',
      title: '[Erro] Pedido duplicado',
      status: 409,
      data: {
        error: ReservationError.Completed,
      },
    },
    {
      id: 'mock-reservation-verify-otp-invalid',
      title: '[Erro] OTP inválido',
      status: 500,
      data: { error: ReservationError.InvalidOtp },
    },
    {
      id: 'mock-reservation-verify-otp-expired',
      title: '[Erro] OTP expirado',
      status: 404,
      data: { error: ReservationError.ExpiredOtp },
    },
    {
      id: 'mock-payment-internal-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {
        message: 'Internal server error',
      },
    },
  ],
}

const MockDebugPurchaseIntention: MockDebug = {
  title: 'Recuperação da Intenção de Compra (Purchase Intention)',
  path: `${REACT_APP_CHECKOUT_BFF_API_URL}/purchase-intentions/:id`,
  method: 'get',
  delay: 3000,
  options: [
    {
      id: 'mock-purchase-intention-installments-2x',
      title: '1x, 2x (SDK)',
      status: 200,
      data: {
        ...NewPurchaseIntentionAPIMock.source,
        payment_options: {
          ...NewPurchaseIntentionAPIMock.source.payment_options,
          [Frequency.THREE_TIMES]: undefined,
          [Frequency.FOUR_TIMES]: undefined,
        },
      },
    },
    {
      id: 'mock-purchase-intention-installments-3x',
      title: '1x, 2x, 3x (SDK)',
      status: 200,
      data: {
        ...NewPurchaseIntentionAPIMock.source,
        payment_options: {
          ...NewPurchaseIntentionAPIMock.source.payment_options,
          [Frequency.FOUR_TIMES]: undefined,
        },
      },
    },
    {
      id: 'mock-purchase-intention-installments',
      title: '1x, 2x, 3x, 4x (SDK)',
      status: 200,
      data: { ...NewPurchaseIntentionAPIMock.source },
    },
    {
      id: 'mock-purchase-intention-sdk',
      title: 'Semanal e mensal (SDK)',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        url_callback: 'https://br.frubana.com/spo/',
      },
    },
    {
      id: 'mock-purchase-intention-trial',
      title: 'Semanal e mensal (Trial)',
      status: 200,
      data: { ...purchaseIntentionAPIMock.source, origin: 'link-trial' },
    },
    {
      id: 'mock-purchase-intention-default-payment-id',
      title: 'Compra com 1 click',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        default_payment_option_id: '28-option-1',
      },
    },
    {
      id: 'mock-purchase-intention-discount',
      title: 'Compra com desconto',
      status: 200,
      data: PurchaseIntentionWithDiscountAPIMock.source,
    },
    {
      id: 'mock-purchase-intention-fraud-suspect',
      title: 'Compra com suspeita de fraude',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        has_pending_otp_verify: true,
      },
    },
    {
      id: 'mock-purchase-intention-cash-payment',
      title: 'Compra a prazo + a vista',
      status: 200,
      data: PurchaseIntentionWithCashPaymentAPIMock.source,
    },
    {
      id: 'mock-purchase-intention-insufficient-limit',
      title: '[Erro 1] Limite insuficiente',
      status: 200,
      data: {
        ...PurchaseIntentionWithCashPaymentAPIMock.source,
        invalidation: {
          error: "merchant's limit is lower than the purchase amount",
          errorCode: PurchaseIntentionErrorCodes.NOT_ENOUGH_LIMIT,
          data: {
            availableLimit: 100000,
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-merchant-not-found',
      title: '[Erro 2] Lojista não encontrado',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'merchant has not been found',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_NOT_FOUND,
          data: {
            email: 'celtino1@tino.com.br',
            merchantDocumentNumber: '09552415000142',
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-merchant-not-approved',
      title: '[Erro 3] Crédito rejeitado',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'merchant is not approved',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_NOT_APPROVED,
          data: {
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-blocked-merchant',
      title: '[Erro 4] Crédito bloqueado',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'merchant blocked',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_BLOCKED,
          data: {
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-blocked-merchant-kyc',
      title: '[Erro 5] Bloqueado por KYC',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'merchant blocked',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_BLOCKED_KYC,
          data: {
            email: 'celtino1@tino.com.br',
            merchantDocumentNumber: '09552415000142',
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-waiting-limit',
      title: '[Erro 6] Limite em analise',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'merchant waiting limit',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_WAITING_LIMIT,
          data: {
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-invalid-amount-cents',
      title: '[Erro 7] Pedido minimo não atingido',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'invalid amount cents',
          errorCode: PurchaseIntentionErrorCodes.MINIMUM_AMOUNT,
          data: {
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-blocked-merchant-bgc',
      title: '[Erro 8] Bloqueado por BGC',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        invalidation: {
          error: 'BGC failed',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_BLOCKED_BGC,
          data: {
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-cash-payment-blocked-merchant',
      title: 'Compra a vista (Crédito rejeitado)',
      status: 200,
      data: {
        ...PurchaseIntentionWithCashPaymentAPIMock.source,
        invalidation: {
          error: 'merchant is not approved',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_NOT_APPROVED,
          data: {
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-cash-payment-merchant-not-found',
      title: 'Compra a prazo + a vista (Lojista não encontrado)',
      status: 200,
      data: {
        ...PurchaseIntentionWithCashPaymentAPIMock.source,
        invalidation: {
          error: 'merchant has not been found',
          errorCode: PurchaseIntentionErrorCodes.MERCHANT_NOT_FOUND,
          data: {
            email: 'celtino1@tino.com.br',
            merchantDocumentNumber: '09552415000142',
            supplierId: JOHN_WICK_SELLER_ID,
          },
        },
      },
    },
    {
      id: 'mock-purchase-intention-status-completed',
      title: 'Pedido já finalizado',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        status: PurchaseIntentionStatus.Completed,
      },
    },
    {
      id: 'mock-purchase-intention-status-expired',
      title: 'Pedido expirado',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        status: PurchaseIntentionStatus.Expired,
      },
    },
    {
      id: 'mock-purchase-intention-status-fraud',
      title: 'Bloqueado por fraude',
      status: 200,
      data: {
        ...purchaseIntentionAPIMock.source,
        status: PurchaseIntentionStatus.VerificationPending,
      },
    },
    {
      id: 'mock-purchase-intention-unauthorized',
      title: '[Erro] Unauthorized',
      status: 401,
      data: {
        message: 'Unauthorized',
      },
    },
    {
      id: 'mock-purchase-intention-interal-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {
        message: 'Internal Server Error',
      },
    },
  ],
}

export const MockDebugCSAT: MockDebug = {
  title: 'Envio de CSAT',
  path: `${REACT_APP_MERCHANT_BFF_API_URL}/v1/company/:id/surveys-responses`,
  method: 'post',
  delay: 2500,
  options: [
    {
      id: 'mock-csat-post-success',
      title: 'Sucesso no envio',
      status: 200,
      data: {},
    },
    {
      id: 'mock-csat-post-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {},
    },
  ],
}

const MockDebugNextBusinessDay: MockDebug = {
  title: 'Próximo dia útil (Next Business Day)',
  path: `${REACT_APP_CHECKOUT_BFF_API_URL}/next-business-day`,
  method: 'get',
  delay: 0,
  options: [
    {
      id: 'mock-next-business-day-success',
      title: 'Próximo dia útil',
      status: 200,
      data: {
        day: '2024-10-07',
      },
    },
    {
      id: 'mock-next-business-day-bad-request',
      title: '[Erro] Bad Request',
      status: 400,
      data: {
        message: 'Bad Request',
      },
    },
    {
      id: 'mock-next-business-day-interal-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {
        message: 'Internal Server Error',
      },
    },
  ],
}

const MockPhones: MockDebug = {
  title: 'Recupera telefones mascarados do lojista',
  path: `${REACT_APP_CHECKOUT_BFF_API_URL}/merchants/:merchantDocumentNumber/contacts`,
  method: 'get',
  delay: 0,
  options: [
    {
      id: 'mock-many-phones-successfully',
      title: '2 telefones',
      status: 200,
      data: [
        {
          id: 'mocked-user-id-1',
          phone: '** **** - 4321',
        },
        {
          id: 'mocked-user-id-2',
          phone: '** **** - 8765',
        },
      ],
    },
    {
      id: 'mock-only-one-phone-successfully',
      title: '1 telefone',
      status: 200,
      data: [
        {
          id: 'mocked-user-id-1',
          phone: '** **** - 4321',
        },
      ],
    },
    {
      id: 'mock-phones-not-found-error',
      title: '[Erro] Not Found',
      status: 404,
      data: { error: 'contacts not found' },
    },
    {
      id: 'mock-phones-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {},
    },
  ],
}

const MockSendCheckoutOTP: MockDebug = {
  title: 'Envia OTP',
  path: `${REACT_APP_AUTH_API_URL}/v1/validate/whatsapp/otp`,
  method: 'post',
  delay: 1000,
  options: [
    {
      id: 'mock-otp-send-valid',
      title: 'Sucesso',
      status: 200,
      data: {},
    },
    {
      id: 'mock-otp-send-bad-request',
      title: '[Erro] Bad Request',
      status: 403,
      data: '',
    },
    {
      id: 'mock-otp-send-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {},
    },
  ],
}

const MockDebugCreateRegister: MockDebug = {
  title: 'Criação de Cadastro Light',
  path: `${REACT_APP_CHECKOUT_BFF_API_URL}/create-soft-register`,
  method: 'post',
  delay: 3000,
  options: [
    {
      id: 'mock-reservation-success',
      title: 'Sucesso',
      status: 200,
      data: {
        merchant_company_id: 'a7a9a33e-83ba-4e47-93dd-b6d3c9255af8',
        user_id: '1119a195-b8ca-4110-87f5-d37821ccad07',
      },
    },
    {
      id: 'mock-reservation-bad-request',
      title: '[Erro] Bad Request',
      status: 400,
      data: {
        message: 'Bad Request',
      },
    },
    {
      id: 'mock-payment-internal-server-error',
      title: '[Erro] Internal Server Error',
      status: 500,
      data: {
        message: 'Internal server error',
      },
    },
  ],
}

export const MockDebugCheckout = [
  MockDebugTerms,
  MockDebugReservation,
  MockDebugCreateRegister,
  MockDebugPurchaseIntention,
  MockDebugCSAT,
  MockDebugNextBusinessDay,
  MockPhones,
  MockSendCheckoutOTP,
]
